import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, Modal } from 'react-bootstrap';

import { getDatasets } from '../offline-features/getDatasets.js';

import { useStateValue } from '../context/state.jsx';
import { setDatasets } from '../context/datasets.js';
import { openAlert } from '../context/alert.js';
import Utils from '../util/Utils.jsx';
import { localIconHrefsFromKml } from '../util/kmlMap.js';

const EditProjectFileModal = ({
	ajax,
	shown,
	editingDatasetId,
	editingType,
	setEditingDataset
}) => {
	const [, dispatch] = useStateValue();

	const [file, setFile] = useState({});
	const [kmlIconWarning, setKmlIconWarning] = useState([]);
	const [p6Projects, setP6Projects] = useState([]);
	const [projId, setProjId] = useState('');

	const editProjectFileChange = e => {
		const nextFile = e.target.files[0];
		if (editingType === 'xer' && nextFile && /\.mpp$/i.test(nextFile.name)) {
			e.target.value = '';
			dispatch(openAlert(
				'Save as XML first',
				'Binary MS Project files (.mpp) are not supported. In MS Project choose File → Save As and pick XML (*.xml), then upload that file.'
			));
			return;
		}
		setFile(nextFile);
		setKmlIconWarning([]);
		if (editingType === 'kml' && nextFile && typeof nextFile.text === 'function') {
			nextFile.text().then(text => {
				setKmlIconWarning(localIconHrefsFromKml(text));
			}).catch(() => {});
		}
	};

	const editFile = e => {
		e.preventDefault();
		const formData = new FormData();
		formData.append(editingType, file);
		formData.append('dataset', editingDatasetId);
		if (editingType === 'xer' && projId) {
			formData.append('projId', projId);
		}

		const urls = {
			xer: 'setNewXerFile',
			kml: 'setNewKmlFile',
			overlay: 'setNewOverlayFile'
		};

		if (!(editingType in urls)) {
			return;
		}
		const url = urls[editingType];

		Utils.ajax(url, formData, dispatch, result => {
			if (result && result.needsProjectChoice) {
				const projects = result.projects || [];
				setP6Projects(projects);
				setProjId(result.preselect ? String(result.preselect) : (projects[0] ? String(projects[0].projId) : ''));
				return;
			}
			close();
			if (result.success) {
				const dropped = result.importWarning && result.importReport && result.importReport.externalLinkCount > 0
					? ` ${result.importWarning}`
					: '';
				const replaced = result.localIconHrefs && result.localIconHrefs.length
					? ' Local icon paths were replaced with standard pins. The original file was kept.'
					: '';
				dispatch(openAlert('Success', 'Project imported successfully.' + replaced + dropped));
				getDatasets(dispatch).then(result => {
					if (result.success) {
						dispatch(setDatasets(result.datasets));
					}
				});
			} else {
				dispatch(openAlert('Error', (result && result.error) || 'Project import failed'));
			}
		});
	};

	const close = () => {
		setFile({});
		setKmlIconWarning([]);
		setP6Projects([]);
		setProjId('');
		setEditingDataset(null);
	};

	if (!editingDatasetId) {
		return null;
	}

	const editingTypeCapitalized = editingType === 'xer' ? 'Schedule' : editingType.toUpperCase();
	const accept = editingType === 'xer'
		? '.xer,.xml,application/xml,text/xml'
		: undefined;
	return (
		<form onSubmit={editFile}>
			<Modal
				show={shown}
				onHide={close}
				container={this}>
				<Modal.Header closeButton>
					<Modal.Title>Edit {editingTypeCapitalized}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormControl
						componentClass="input"
						onChange={editProjectFileChange}
						type="file"
						accept={accept}
						label={`${editingTypeCapitalized} File`} />
					{editingType === 'xer' && (
						<div className="help-block" style={{ marginTop: 8, fontSize: 12 }}>
							Primavera P6 <code>.xer</code> or MS Project XML (File → Save As → XML). One P6 project is imported from each file.
						</div>
					)}
					{editingType === 'xer' && p6Projects.length > 0 && (
						<div style={{ marginTop: 12 }}>
							<p>This file has more than one P6 project. Choose the one to import. The current schedule stays until you confirm.</p>
							<FormControl
								componentClass="select"
								value={projId}
								onChange={e => setProjId(e.target.value)}
								aria-label="P6 project">
								{p6Projects.map(project => (
									<option key={project.projId} value={project.projId}>
										{project.shortName || `Project ${project.projId}`}
										{project.dataDate ? ` — ${project.dataDate}` : ''}
										{` — ${project.taskCount || 0} activities`}
									</option>
								))}
							</FormControl>
						</div>
					)}
					{editingType === 'kml' && kmlIconWarning.length > 0 && (
						<div className="alert alert-warning" role="status" style={{ marginTop: 8 }}>
							<p>
								This KML references local icon files the browser cannot load.
								They will be replaced with standard map pins. The original file is kept.
							</p>
							<ul>
								{kmlIconWarning.map(href => <li key={href}>{href}</li>)}
							</ul>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={close}>Cancel</Button>
					<Button bsStyle="primary" onClick={editFile}>Okay</Button>
				</Modal.Footer>
			</Modal>
		</form>
	);
};

export default EditProjectFileModal;

EditProjectFileModal.propTypes = {
	ajax: PropTypes.func,
	shown: PropTypes.bool,
	editingDatasetId: PropTypes.string,
	editingType: PropTypes.string,
	setEditingDataset: PropTypes.func
};